// src/pages/Archive/OperatorReports.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility,
  PictureAsPdf,
  TableChart,
  Search,
  Refresh,
  Download,
  Edit,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { getData, saveData } from '../../services/storageService';
import ExportService from '../../services/exportService';

const OperatorReports = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  
  // Додаємо функцію для fallback перекладів
  const tt = (key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    shift: 'all',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // Перевірка прав адміністратора (тимчасово - завжди true для тесту)
  const isAdmin = true;

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allRecords = await getData('lainsa_records') || [];
      
      // Фільтруємо тільки звіти операторів (shift reports)
      const operatorReports = allRecords.filter(record => 
        record.recordType === 'shift_report' || 
        (record.tunel && record.rech_tunel) // Емпіричний критерій для старих записів
      );

      // Сортуємо за датою - найновіші зверху
      const sortedReports = operatorReports.sort((a, b) => {
        const dateA = new Date(a.fecha || a.date || a.createdAt || 0);
        const dateB = new Date(b.fecha || b.date || b.createdAt || 0);
        return dateB - dateA; // Сортування за спаданням (новіші зверху)
      });
      
      console.log('📊 Operator reports found:', sortedReports.length);
      setReports(sortedReports);
      setFilteredReports(sortedReports);
    } catch (err) {
      setError(tt('archive.load_error', 'Error cargando reportes'));
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    let result = reports;

    // Filter by date range
    if (filters.dateFrom) {
      result = result.filter(report => new Date(report.fecha || report.date) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      result = result.filter(report => new Date(report.fecha || report.date) <= new Date(filters.dateTo));
    }

    // Filter by shift
    if (filters.shift !== 'all') {
      result = result.filter(report => (report.turno || report.shift) === filters.shift);
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(report =>
        JSON.stringify(report).toLowerCase().includes(searchLower) ||
        (report.id && report.id.toLowerCase().includes(searchLower)) ||
        (report.operators && report.operators.join(' ').toLowerCase().includes(searchLower))
      );
    }

    setFilteredReports(result);
    setCurrentPage(1);
  }, [reports, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleViewReport = (reportId) => {
    navigate(`/view/${encodeURIComponent(reportId)}`);
  };

  const handleEditReport = (reportId) => {
    navigate(`/view/${encodeURIComponent(reportId)}`);
  };

  const handleDeleteReport = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      const allRecords = await getData('lainsa_records') || [];
      const updatedRecords = allRecords.filter(r => r.id !== reportToDelete.id);
      
      await saveData('lainsa_records', updatedRecords);
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      alert('Registro eliminado correctamente');
      loadReports(); // Перезавантажити список
    } catch (error) {
      console.error('Error eliminando registro:', error);
      alert('Error al eliminar el registro');
    }
  };

  const handleExportPdf = (report) => {
    try {
      ExportService.exportReportToPDF(report);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert(tt('archive.export_pdf_error', 'Error al exportar PDF'));
    }
  };

  const handleExportExcel = () => {
    try {
      ExportService.exportReportsToExcel(filteredReports, filters);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert(tt('archive.export_excel_error', 'Error al exportar Excel'));
    }
  };

  const handleBulkExport = (format) => {
    if (filteredReports.length === 0) {
      alert(tt('archive.no_data_export', 'No hay datos para exportar'));
      return;
    }

    try {
      ExportService.bulkExport(filteredReports, format, filters);
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
      alert(tt('archive.export_error', 'Error al exportar'));
    }
  };

  const getShiftColor = (shift) => {
    const colors = {
      mañana: 'success',
      tarde: 'warning',
      noche: 'secondary'
    };
    return colors[shift] || 'default';
  };

  // Функція для отримання загальної ваги з різних форматів даних
  const getTotalWeight = (report, type = 'production') => {
    const data = type === 'production' ? report.tunel : report.rech_tunel;
    
    if (!data) return 0;
    
    // Спроба отримати total
    if (data.total !== undefined && data.total !== null) {
      return data.total;
    }
    
    // Якщо total немає, обчислюємо суму всіх значень
    const values = Object.values(data).filter(val => 
      typeof val === 'number' && !isNaN(val) && val > 0
    );
    
    return values.reduce((sum, val) => sum + val, 0);
  };

  // Pagination
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const currentPageData = filteredReports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TableChart />
        {tt('archive.operator_reports', 'Reportes de Operador')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        {tt('archive.operator_reports_desc', 'Historial completo de reportes de producción y turnos')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Desde fecha"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Hasta fecha"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Turno</InputLabel>
                <Select
                  value={filters.shift}
                  label="Turno"
                  onChange={(e) => handleFilterChange('shift', e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="mañana">Mañana</MenuItem>
                  <MenuItem value="tarde">Tarde</MenuItem>
                  <MenuItem value="noche">Noche</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Buscar..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleBulkExport('excel')}
                disabled={filteredReports.length === 0}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={() => handleBulkExport('pdf')}
                disabled={filteredReports.length === 0}
              >
                Exportar PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadReports}
              >
                Actualizar
              </Button>
            </Box>
            <Button
              variant="text"
              onClick={() => {
                setFilters({
                  dateFrom: '',
                  dateTo: '',
                  shift: 'all',
                  search: ''
                });
              }}
            >
              Limpiar Filtros
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Resultados ({filteredReports.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tt('archive.total_found', 'Total encontrados')}: {filteredReports.length}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Turno</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Operadores</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Producción (kg)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Rechazo (kg)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageData.map((report) => {
                  const productionTotal = getTotalWeight(report, 'production');
                  const rejectionTotal = getTotalWeight(report, 'rejection');
                  
                  // Форматуємо ID для відображення
                  const displayId = report.id.startsWith('shift_') 
                    ? `shift_${report.id.split('_')[1]?.substring(0, 4)}...` 
                    : `${report.id.substring(0, 8)}...`;
                  
                  return (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {displayId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(report.fecha || report.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={report.turno || report.shift || 'No especificado'}
                          color={getShiftColor(report.turno || report.shift)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {report.operators?.join(', ') || 'No especificado'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {productionTotal} kg
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error.main">
                          {rejectionTotal} kg
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Ver detalles">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewReport(report.id)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAdmin && (
                            <>
                              <Tooltip title="Editar reporte">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleEditReport(report.id)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar reporte">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteReport(report)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Exportar PDF">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleExportPdf(report)}
                            >
                              <PictureAsPdf fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredReports.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                {tt('archive.no_reports', 'No hay reportes disponibles')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {tt('archive.no_reports_desc', 'Los reportes de turno aparecerán aquí después de cerrar un turno')}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {filteredReports.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar el reporte del {reportToDelete ? new Date(reportToDelete.fecha || reportToDelete.date).toLocaleDateString() : ''} - {reportToDelete?.turno || reportToDelete?.shift}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorReports;


